import React, { useContext, useEffect, useRef, useState } from "react";
import {
  FiEdit2,
  FiCamera,
  FiCheck,
  FiX,
  FiAward,
  FiBriefcase,
  FiBookOpen,
  FiFolder,
  FiHeart,
  FiUserCheck,
  FiUsers,
  FiFileText,
  FiTrash2,
} from "react-icons/fi";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import AppShell from "../components/layout/AppShell";
import { AuthContext } from "../context/AuthContext";
import { CommunityContext } from "../context/CommunityContext";
import { useToast } from "../context/ToastContext";
import { Avatar, Button, Card, Input, Textarea, Spinner } from "../components/ui";
import EditableListSection from "../components/common/EditableListSection";
import { getProfile, updateProfile } from "../api/bios";
import { updateUserInfo } from "../api/users";
import { getUserPosts } from "../api/posts";
import uploadUrl from "../utils/assetUrl";
import { coerceArray } from "../utils/format";

const SECTIONS = [
  { key: "skills", label: "Skills", icon: FiAward, pill: true, placeholder: "Add a skill" },
  { key: "employment", label: "Experience", icon: FiBriefcase, placeholder: "Role - Company - Year" },
  { key: "education", label: "Education", icon: FiBookOpen, placeholder: "School - Award - Year" },
  { key: "projects", label: "Projects", icon: FiFolder, placeholder: "Project name - short description" },
  { key: "volunteer", label: "Volunteer experience", icon: FiHeart, placeholder: "Role - Organization - Year" },
  { key: "referees", label: "Referees", icon: FiUserCheck, placeholder: "Name - Contact - Relationship" },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Profile() {
  const { user, updateUser } = useContext(AuthContext);
  const { joinedCommunities } = useContext(CommunityContext);
  const { notify } = useToast();
  const fileRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [postCount, setPostCount] = useState(0);

  const [bioText, setBioText] = useState("");
  const [fields, setFields] = useState({
    skills: [],
    employment: [],
    education: [],
    projects: [],
    volunteer: [],
    referees: [],
  });
  const [profileImage, setProfileImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removePhoto, setRemovePhoto] = useState(false);

  // Core account fields — separate from the bio/portfolio fields above,
  // since they're saved through a different endpoint (PATCH /users/update)
  // rather than the bio endpoint.
  const [accountForm, setAccountForm] = useState({ name: "", lastName: "", email: "", phone: "" });
  const [accountErrors, setAccountErrors] = useState({});

  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    getProfile(user.id)
      .then((res) => {
        if (res.data?.success) {
          const p = res.data.profile || {};
          setBioText(p.Bio || "");
          setFields({
            skills: coerceArray(p.skills),
            employment: coerceArray(p.employment),
            education: coerceArray(p.education),
            projects: coerceArray(p.projects),
            volunteer: coerceArray(p.volunteer),
            referees: coerceArray(p.referees || p.refereesss),
          });
          setProfileImage(p.profileImage || null);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));

    getUserPosts(user.id)
      .then((res) => {
        setPostCount((res.data?.data || []).length);
      })
      .catch(() => {});
  }, [user?.id]);

  const beginEditing = () => {
    setAccountForm({
      name: user?.name || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      phone: user?.phone || "",
    });
    setAccountErrors({});
    setRemovePhoto(false);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setImageFile(null);
    setRemovePhoto(false);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
    }
  };

  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setRemovePhoto(false);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemovePhoto = () => {
    setImageFile(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    setRemovePhoto(true);
  };

  const validateAccountForm = () => {
    const errors = {};
    if (!accountForm.name.trim()) errors.name = "First name is required.";
    if (!accountForm.lastName.trim()) errors.lastName = "Last name is required.";
    if (!accountForm.email.trim()) errors.email = "Email is required.";
    else if (!EMAIL_REGEX.test(accountForm.email.trim())) errors.email = "Enter a valid email address.";
    if (!accountForm.phone) errors.phone = "Phone number is required.";
    else if (!isValidPhoneNumber(accountForm.phone)) errors.phone = "Enter a valid phone number.";
    setAccountErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateAccountForm()) return;
    setSaving(true);
    try {
      // Account info first — if the email/phone conflicts with another
      // account or fails validation, stop here rather than half-saving
      // (bio changes going through while account changes silently don't
      // would be a confusing partial-save).
      await updateUserInfo(user.id, accountForm);
      updateUser({
        name: accountForm.name.trim(),
        lastName: accountForm.lastName.trim(),
        email: accountForm.email.trim(),
        phone: accountForm.phone,
      });

      const formData = new FormData();
      formData.append("Bio", bioText);
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, JSON.stringify(value));
      });
      if (imageFile) formData.append("profileImage", imageFile);
      if (removePhoto) formData.append("removeProfileImage", "true");

      const res = await updateProfile(formData);
      // Use the server's actual response rather than guessing at the new
      // state locally — this used to just null out profileImage and leave
      // the local blob preview in place, discarding what the server
      // actually saved.
      setProfileImage(res.data?.profile?.profileImage ?? null);

      notify("Your profile has been updated.", "success");
      cancelEditing();
    } catch (error) {
      const message = error.response?.data?.message || "We couldn't save your profile. Please try again.";
      notify(message, "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppShell title="Profile" wide>
        <div className="flex justify-center py-24">
          <Spinner className="h-8 w-8" />
        </div>
      </AppShell>
    );
  }

  const avatarSrc = imagePreview || (!removePhoto && profileImage ? uploadUrl(profileImage) : null);

  return (
    <AppShell title="Your profile" subtitle="Your professional portfolio on Empact Alumni" wide>
      <Card className="overflow-hidden">
        <div className="h-32 bg-gradient-to-r from-primary-600 to-primary-800 sm:h-40" />
        <div className="px-5 pb-5 sm:px-8 sm:pb-8">
          <div className="-mt-12 flex flex-col items-start gap-4 sm:-mt-14 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex items-end gap-4">
              <div className="relative">
                <Avatar
                  src={avatarSrc}
                  name={`${user?.name || ""} ${user?.lastName || ""}`}
                  size="xl"
                  ring
                />
                {editing && (
                  <div className="absolute -bottom-1 -right-1 flex gap-1">
                    <button
                      onClick={() => fileRef.current?.click()}
                      className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-white shadow-card hover:bg-primary-700"
                      aria-label="Change profile photo"
                      title="Change photo"
                    >
                      <FiCamera className="h-4 w-4" />
                    </button>
                    {avatarSrc && (
                      <button
                        onClick={handleRemovePhoto}
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-red-500 shadow-card ring-1 ring-red-100 hover:bg-red-50"
                        aria-label="Remove profile photo"
                        title="Remove photo"
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImagePick} />
              </div>
              {!editing && (
                <div className="pb-0">
                  <h2 className="text-xl font-bold text-slate-900">
                    {user?.name} {user?.lastName}
                  </h2>
                  <p className="text-sm text-slate-500">{user?.email}</p>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              {editing ? (
                <>
                  <Button variant="secondary" size="sm" onClick={cancelEditing} disabled={saving}>
                    <FiX className="h-4 w-4" /> Cancel
                  </Button>
                  <Button size="sm" onClick={handleSave} loading={saving}>
                    <FiCheck className="h-4 w-4" /> Save changes
                  </Button>
                </>
              ) : (
                <Button variant="secondary" size="sm" onClick={beginEditing}>
                  <FiEdit2 className="h-4 w-4" /> Edit profile
                </Button>
              )}
            </div>
          </div>

          {editing && (
            <div className="mt-6 grid grid-cols-1 gap-4 border-t border-slate-100 pt-6 sm:grid-cols-2">
              <Input
                label="First name"
                value={accountForm.name}
                onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })}
                error={accountErrors.name}
              />
              <Input
                label="Last name"
                value={accountForm.lastName}
                onChange={(e) => setAccountForm({ ...accountForm, lastName: e.target.value })}
                error={accountErrors.lastName}
              />
              <Input
                label="Email address"
                type="email"
                value={accountForm.email}
                onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })}
                error={accountErrors.email}
              />
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Phone number</label>
                <PhoneInput
                  international
                  defaultCountry="KE"
                  value={accountForm.phone}
                  onChange={(value) => setAccountForm({ ...accountForm, phone: value || "" })}
                  className={`h-10 rounded-lg border px-3 ${accountErrors.phone ? "border-red-300" : "border-slate-200"}`}
                />
                {accountErrors.phone && <p className="mt-1.5 text-xs text-red-600">{accountErrors.phone}</p>}
              </div>
            </div>
          )}

          <div className="mt-6 grid grid-cols-3 gap-3 border-t border-slate-100 pt-5 sm:max-w-sm">
            <Stat icon={<FiUsers />} value={joinedCommunities.length} label="Communities" />
            <Stat icon={<FiFileText />} value={postCount} label="Posts" />
            <Stat icon={<FiAward />} value={fields.skills.length} label="Skills" />
          </div>
        </div>
      </Card>

      <Card className="mt-6 p-5 sm:p-6">
        <h3 className="text-sm font-semibold text-slate-900">About</h3>
        {editing ? (
          <Textarea
            value={bioText}
            onChange={(e) => setBioText(e.target.value)}
            rows={4}
            className="mt-3"
            placeholder="Tell the community about yourself..."
          />
        ) : (
          <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-600">
            {bioText || "This member hasn't written a bio yet."}
          </p>
        )}
      </Card>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {SECTIONS.map(({ key, label, icon: Icon, pill, placeholder }) => (
          <Card key={key} className="p-5 sm:p-6">
            <div className="mb-3 flex items-center gap-2">
              <Icon className="h-4 w-4 text-primary-500" />
              <h3 className="text-sm font-semibold text-slate-900">{label}</h3>
            </div>
            <EditableListSection
              items={fields[key]}
              onChange={(next) => setFields((f) => ({ ...f, [key]: next }))}
              editing={editing}
              pillStyle={pill}
              placeholder={placeholder}
            />
          </Card>
        ))}
      </div>
    </AppShell>
  );
}

function Stat({ icon, value, label }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2.5 text-center">
      <div className="flex items-center justify-center gap-1.5 text-primary-600">{icon}</div>
      <p className="mt-1 text-lg font-bold text-slate-900">{value}</p>
      <p className="text-[11px] text-slate-500">{label}</p>
    </div>
  );
}
